import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Phone, User, Building2, Clock, Search, Filter,
  X, CheckCircle, AlertCircle, MessageSquare, FileText, Calendar, ArrowLeft
} from 'lucide-react';
import { adminVerify } from '../../services/api';
import { listContactSubmissions, updateContactSubmission } from '../../services/api';
const statusOptions = ['All', 'New', 'In Progress', 'Resolved', 'Closed'];

const AdminContactPage = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [filters, setFilters] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    status: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [isUpdatingNotes, setIsUpdatingNotes] = useState(false);
  useEffect(() => {
    const init = async () => {
      try {
        await adminVerify();
        await refresh();
      } catch (e) {
        navigate('/admin/login');
      }
    };
    init();
  }, [navigate]);

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      };
      
      // Remove empty filters and handle 'All' status
      Object.keys(params).forEach(key => {
        if (!params[key] || params[key] === 'All') delete params[key];
      });
      
      const data = await listContactSubmissions(params);
      
      // Handle response format
      let submissionsList = [];
      let paginationInfo = null;
      
      if (Array.isArray(data)) {
        submissionsList = data;
      } else if (data.submissions) {
        submissionsList = data.submissions;
        paginationInfo = {
          total: data.total || 0,
          totalPages: data.totalPages || 0,
          page: data.page || 1
        };
      } else {
        submissionsList = [];
      }
      
      setSubmissions(submissionsList);
      
      // Update pagination if data has pagination info
      if (paginationInfo) {
        setPagination(prev => ({
          ...prev,
          total: paginationInfo.total,
          totalPages: paginationInfo.totalPages
        }));
      }
    } catch (e) {
      setError(e.message || 'Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  const handleStatusChange = async (id, status) => {
    try {
      await updateContactSubmission(id, { status });
      setSuccess(`Status updated to ${status}`);
      await refresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to update status');
    }
  };

  const handleSaveNotes = async () => {
    if (!selected) return;
    setIsUpdatingNotes(true);
    try {
      await updateContactSubmission(selected._id, { notes });
      setSuccess('Notes saved successfully');
      await refresh();
      setTimeout(() => setSuccess(''), 3000);
      // Update selected to reflect the saved notes
      setSelected((prev) => prev ? { ...prev, notes } : prev);
    } catch (e) {
      setError(e.message || 'Failed to save notes');
    } finally {
      setIsUpdatingNotes(false);
    }
  };

  // Submissions are already filtered by backend
  const filteredSubmissions = submissions;

  const formatDateTime = (value) => {
    if (!value) return '—';
    try {
      const d = new Date(value);
      return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return value;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'In Progress': return 'bg-amber-100 text-amber-800';
      case 'Resolved': return 'bg-green-100 text-green-800';
      case 'Closed': return 'bg-gray-200 text-gray-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  useEffect(() => {
    if (selected) {
      setNotes(selected.notes || '');
    }
  }, [selected]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/admin')} 
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{'Contact Enquiries'}</h1>
              <p className="text-xs text-gray-500">Manage all website contact form submissions</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border-l-4 border-red-500 text-red-700 flex items-center justify-between shadow-sm">
            <span className="font-medium">{error}</span>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 rounded-xl bg-green-50 border-l-4 border-green-500 text-green-700 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">{success}</span>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">{'Filters'}</h3>
              {Object.values(filters).some(f => f) && (
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                  {Object.values(filters).filter(f => f).length} active
                </span>
              )}
            </div>
            {Object.values(filters).some(f => f) && (
              <button
                onClick={() => {
                  setFilters({
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    status: '',
                    startDate: '',
                    endDate: ''
                  });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                {'Clear All'}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {/* Name Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Name</label>
              <div className="relative">
                <User className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.name ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search name..."
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                />
                {filters.name && (
                  <button
                    onClick={() => handleFilterChange('name', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Email Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.email ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search email..."
                  value={filters.email}
                  onChange={(e) => handleFilterChange('email', e.target.value)}
                />
                {filters.email && (
                  <button
                    onClick={() => handleFilterChange('email', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Phone Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Phone</label>
              <div className="relative">
                <Phone className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.phone ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search phone..."
                  value={filters.phone}
                  onChange={(e) => handleFilterChange('phone', e.target.value)}
                />
                {filters.phone && (
                  <button
                    onClick={() => handleFilterChange('phone', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Subject Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Subject</label>
              <div className="relative">
                <FileText className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.subject ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search subject..."
                  value={filters.subject}
                  onChange={(e) => handleFilterChange('subject', e.target.value)}
                />
                {filters.subject && (
                  <button
                    onClick={() => handleFilterChange('subject', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Status</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                {statusOptions.filter(opt => opt !== 'All').map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">From Date</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">To Date</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                min={filters.startDate}
              />
            </div>
          </div>

        </div>


        {/* List */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Contact Submissions</h3>
              <p className="text-sm text-gray-500 mt-1">
                {pagination.total || filteredSubmissions.length} submission{(pagination.total || filteredSubmissions.length) !== 1 ? 's' : ''} total
              </p>
            </div>
          </div>

          {loading ? (
            <div className="p-10 text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mb-3"></div>
              <p className="text-gray-600">Loading submissions...</p>
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="p-10 text-center">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-1 font-medium">No submissions found.</p>
              <p className="text-sm text-gray-500">Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredSubmissions.map((s) => (
                <button
                  key={s._id}
                  type="button"
                  onClick={() => setSelected(s)}
                  className="w-full text-left p-6 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 transition-all flex items-start justify-between gap-4"
                >
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="font-semibold text-gray-900">{s.name || 'Unnamed'}</span>
                          {s.company && (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                              <Building2 className="h-3 w-3" />
                              {s.company}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-1">{s.subject}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(s.status)}`}>
                        {s.status || 'New'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-600 mt-2">
                      <div className="flex items-center gap-2">
                        <Mail className="h-3 w-3 text-gray-400" />
                        <span>{s.email}</span>
                      </div>
                      {s.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-3 w-3 text-gray-400" />
                          <span>{s.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Clock className="h-3 w-3 text-gray-400" />
                        <span>{formatDateTime(s.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-8 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total || filteredSubmissions.length} total)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Details Drawer / Modal */}
        {selected && (
          <div className="fixed inset-0 z-[1000] flex items-stretch sm:items-center justify-end bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="bg-white shadow-2xl w-full sm:w-[92vw] md:w-[82vw] lg:w-[70vw] xl:w-[60vw] 2xl:w-[52vw] max-w-[960px] h-full sm:h-[calc(100vh-32px)] sm:my-4 flex flex-col rounded-none sm:rounded-2xl overflow-x-hidden min-w-0">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">Contact Details</h3>
                  <p className="text-xs text-gray-500">Submitted on {formatDateTime(selected.createdAt)}</p>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Name</div>
                  <div className="flex items-center gap-2 font-medium text-gray-900">
                    <User className="h-4 w-4 text-gray-400" />
                    {selected.name || '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Email</div>
                  <div className="flex items-center gap-2 text-gray-900">
                    <Mail className="h-4 w-4 text-gray-400" />
                    {selected.email || '—'}
                  </div>
                </div>
                {selected.phone && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Phone</div>
                    <div className="flex items-center gap-2 text-gray-900">
                      <Phone className="h-4 w-4 text-gray-400" />
                      {selected.phone}
                    </div>
                  </div>
                )}
                {selected.company && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Company / Organization</div>
                    <div className="flex items-center gap-2 text-gray-900">
                      <Building2 className="h-4 w-4 text-gray-400" />
                      {selected.company}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs text-gray-500 mb-1">Subject</div>
                <div className="font-semibold text-gray-900">{selected.subject}</div>
              </div>

              <div>
                <div className="text-xs text-gray-500 mb-1">Message</div>
                <div className="mt-1 p-4 bg-gray-50 rounded-xl text-sm text-gray-800 whitespace-pre-wrap">
                  {selected.message}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Status</div>
                  <select
                    className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={selected.status || 'New'}
                    onChange={async (e) => {
                      const newStatus = e.target.value;
                      await handleStatusChange(selected._id, newStatus);
                      setSelected((prev) => prev ? { ...prev, status: newStatus } : prev);
                    }}
                  >
                    {statusOptions.filter(s => s !== 'All').map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                {selected.source && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Source</div>
                    <div className="text-sm text-gray-800">{selected.source}</div>
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs text-gray-500 mb-2 font-semibold">Admin Notes</div>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none"
                  rows={4}
                  placeholder="Add notes about this contact submission..."
                />
                <button
                  onClick={handleSaveNotes}
                  disabled={isUpdatingNotes}
                  className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdatingNotes ? 'Saving...' : 'Save Notes'}
                </button>
              </div>
            </div>

            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gray-50 rounded-b-2xl">
              <div className="text-xs text-gray-500">
                Last updated: {formatDateTime(selected.updatedAt || selected.createdAt)}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                {/*
  <a
    href={`mailto:${selected.email}`}
    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-2"
  >
    <Mail className="h-4 w-4" />
    Reply
  </a>
  */}

                {/*
  {selected.phone && (
    <a
      href={`tel:${selected.phone}`}
      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2"
    >
      <Phone className="h-4 w-4" />
      Call
    </a>
  )}
  */}

                <button
                  onClick={() => {
                    setSelected(null);
                    setNotes('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium w-full sm:w-auto"
                >
                  Close
                </button>
              </div>

            </div>
          </div>
        </div>
        )}
      </main>
    </div>
  );
};

export default AdminContactPage;
